import React, { lazy, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router'
import { withAuthenticationRequired } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import Layout from '@/ui/shared/layout'
import { PickScope, StoreOnly } from '@/ui/shared'
import { Home } from '@/ui/pages'
import OrdersPage from './ui/pages/returns/orders.page'

const SizingPage = lazy(() => import('@/ui/pages/sizing/sizing.page'))
const SizingModelPage = lazy(
  () => import('@/ui/pages/sizing/sizing-model.page')
)
const ReturnsAnalytics = lazy(
  () => import('@/ui/pages/analytics/returns-analytics.page')
)
const ReturnRates = lazy(() => import('@/ui/pages/analytics/return-rates.page'))
const ProductAnalytics = lazy(
  () => import('@/ui/pages/analytics/product-analytics.page')
)
const ProductsReturnRates = lazy(
  () => import('@/ui/pages/analytics/product-return-rates.page')
)
const ReturnedProducts = lazy(
  () => import('@/ui/pages/analytics/returned-products.page')
)
const ProductDetails = lazy(
  () => import('@/ui/pages/analytics/product-details.page')
)
const Billing = lazy(() => import('@/ui/pages/billing/billing.page'))
const Referrals = lazy(() => import('@/ui/pages/account/referrals.page'))
const ReverStore = lazy(() => import('@/ui/pages/account/rever-store.page'))
const Users = lazy(() => import('@/ui/pages/account/users.page'))

const Items = lazy(() => import('@/ui/pages/returns/items.page'))
const ProcessDetails = lazy(
  () => import('@/ui/pages/returns/process-details.page')
)

const Copies = lazy(() => import('@/ui/pages/configuration/copies.page'))
const Withdrawals = lazy(() => import('@/ui/pages/returns/withdrawals.page'))
const WithdrawalSettings = lazy(
  () => import('@/ui/pages/configuration/withdrawal.page')
)
const Theme = lazy(() => import('@/ui/pages/configuration/theme.page'))
const Shipping = lazy(() => import('@/ui/pages/configuration/shipping.page'))
const Onboarding = lazy(
  () => import('@/ui/pages/configuration/onboarding.page')
)
// Return conditions, reasons, requirements and refunds: per store, or per group once on their v2 flags.
const ReturnsAndRefunds = lazy(
  () => import('@/ui/pages/configuration/returns-and-refunds.page')
)
const Warehouses = lazy(
  () => import('@/ui/pages/configuration/warehouses.page')
)
const Exchanges = lazy(() => import('@/ui/pages/configuration/exchanges.page'))

const RateCard = lazy(() => import('@/ui/pages/logistics/rate-card.page'))

const NotFound = lazy(() => import('@/ui/pages/404.page'))

const Integrations = lazy(
  () => import('@/ui/pages/integrations/integrations.page')
)
const IntegrationDetails = lazy(
  () => import('@/ui/pages/integrations/integration-details.page')
)

type ProtectedRouteProps = {
  component: React.ComponentType<unknown>
}
const ProtectedRoute = ({ component, ...args }: ProtectedRouteProps) => {
  const ComponentWithAuth = withAuthenticationRequired(component, args)
  return <ComponentWithAuth />
}

// Keeps bookmarks to pages that moved working; the query carries the selected ecommerce.
const MovedTo = ({ to }: { to: string }) => {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

export default function App() {
  useEffect(() => {
    const glitchReport = async (e: KeyboardEvent) => {
      if (e.key === 'g' && (e.metaKey || e.ctrlKey)) {
        const feedback = Sentry.feedbackIntegration({
          enableScreenshot: true,
          autoInject: false,
          colorScheme: 'dark',
          themeDark: {
            background: '#020c1c'
          },
          themeLight: {
            background: '#020c1c'
          },
          showBranding: false
        })
        const form = await feedback.createForm()
        form.appendToDom()
        form.open()
      }
    }

    document.addEventListener('keydown', glitchReport)
    return () => document.removeEventListener('keydown', glitchReport)
  }, [])

  return (
    <Routes>
      <Route path="/onboarding" element={<Onboarding />} />
      <Route element={<Layout />}>
        <Route path="/" element={<ProtectedRoute component={Home} />} />
        <Route path="/returns">
          <Route index element={<ProtectedRoute component={NotFound} />} />
          <Route
            index
            path="orders"
            element={<ProtectedRoute component={OrdersPage} />}
          />
          <Route path="items" element={<ProtectedRoute component={Items} />} />
          <Route
            path="orders/:id"
            element={<ProtectedRoute component={ProcessDetails} />}
          />
          <Route
            path="withdrawals"
            element={
              <StoreOnly>
                <ProtectedRoute component={Withdrawals} />
              </StoreOnly>
            }
          />
        </Route>

        <Route path="/size-suggestions">
          <Route
            index
            element={
              <StoreOnly>
                <ProtectedRoute component={SizingPage} />
              </StoreOnly>
            }
          />
          <Route
            path=":id"
            element={
              <StoreOnly>
                <ProtectedRoute component={SizingModelPage} />
              </StoreOnly>
            }
          />
        </Route>

        <Route path="/analytics">
          <Route
            index
            path="compensation"
            element={
              <PickScope>
                <ProtectedRoute component={ReturnsAnalytics} />
              </PickScope>
            }
          />
          <Route
            path="return-rates"
            element={
              <PickScope>
                <ProtectedRoute component={ReturnRates} />
              </PickScope>
            }
          />
          <Route
            path="products"
            element={
              <PickScope>
                <ProtectedRoute component={ProductAnalytics} />
              </PickScope>
            }
          />
          <Route
            path="products/:id"
            element={
              <PickScope>
                <ProtectedRoute component={ProductDetails} />
              </PickScope>
            }
          />
          <Route
            path="products-return-rates"
            element={
              <PickScope>
                <ProtectedRoute component={ProductsReturnRates} />
              </PickScope>
            }
          />
          <Route
            path="all-products"
            element={
              <PickScope>
                <ProtectedRoute component={ReturnedProducts} />
              </PickScope>
            }
          />
        </Route>

        <Route path="/configuration">
          <Route
            index
            path="theme"
            element={
              <StoreOnly>
                <ProtectedRoute component={Theme} />
              </StoreOnly>
            }
          />
          <Route
            path="copies"
            element={
              <StoreOnly>
                <ProtectedRoute component={Copies} />
              </StoreOnly>
            }
          />
          <Route
            path="withdrawal"
            element={
              <StoreOnly>
                <ProtectedRoute component={WithdrawalSettings} />
              </StoreOnly>
            }
          />
          <Route
            path="shipping"
            element={
              <StoreOnly>
                <ProtectedRoute component={Shipping} />
              </StoreOnly>
            }
          />
          <Route
            path="reasons"
            element={<ProtectedRoute component={ReturnsAndRefunds} />}
          />
          <Route
            path="non-returnable"
            element={<ProtectedRoute component={ReturnsAndRefunds} />}
          />
          <Route
            path="warehouses"
            element={<ProtectedRoute component={Warehouses} />}
          />
          <Route
            path="warehouses/rules"
            element={<ProtectedRoute component={Warehouses} />}
          />
          <Route
            path="exchanges"
            element={<ProtectedRoute component={Exchanges} />}
          />
          <Route
            path="exchanges/orders"
            element={<ProtectedRoute component={Exchanges} />}
          />
          <Route
            path="exchanges/full-catalog"
            element={<ProtectedRoute component={Exchanges} />}
          />
          <Route
            path="exchanges/instant-exchange"
            element={<ProtectedRoute component={Exchanges} />}
          />
          <Route
            path="return-conditions"
            element={<ProtectedRoute component={ReturnsAndRefunds} />}
          />
          <Route
            path="requirements"
            element={<ProtectedRoute component={ReturnsAndRefunds} />}
          />
          <Route
            path="refunds"
            element={<ProtectedRoute component={ReturnsAndRefunds} />}
          />
          <Route path="users" element={<MovedTo to="/account/users" />} />
          <Route
            path="webhooks"
            element={<MovedTo to="/account/integrations/webhooks" />}
          />
        </Route>

        <Route path="/account">
          <Route path="integrations" element={<Integrations />} />
          <Route
            path="integrations/webhooks"
            element={<ProtectedRoute component={Integrations} />}
          />
          <Route
            path="integrations/api-keys"
            element={<ProtectedRoute component={Integrations} />}
          />
          <Route
            path="integrations/mcp"
            element={<ProtectedRoute component={Integrations} />}
          />
          <Route
            path="integrations/credentials"
            element={<ProtectedRoute component={Integrations} />}
          />
          <Route
            path="integrations/platform"
            element={<ProtectedRoute component={Integrations} />}
          />
          <Route
            path="integrations/warehouses"
            element={<ProtectedRoute component={Integrations} />}
          />
          <Route
            path="integrations/apps"
            element={<ProtectedRoute component={Integrations} />}
          />
          <Route
            path="integrations/carriers"
            element={<ProtectedRoute component={Integrations} />}
          />
          <Route
            path="integrations/details/:id"
            element={
              <StoreOnly>
                <IntegrationDetails />
              </StoreOnly>
            }
          />
          <Route
            path="store"
            element={<ProtectedRoute component={ReverStore} />}
          />
          <Route
            path="referrals"
            element={<ProtectedRoute component={Referrals} />}
          />
          <Route path="users" element={<ProtectedRoute component={Users} />} />
          <Route path="billing" element={<MovedTo to="/billing" />} />
        </Route>

        <Route
          path="/billing"
          element={<ProtectedRoute component={Billing} />}
        />

        <Route
          path="/rate-card"
          element={<ProtectedRoute component={RateCard} />}
        />

        <Route path="*" element={<ProtectedRoute component={NotFound} />} />
      </Route>
    </Routes>
  )
}
